import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { MatDialog } from '@angular/material/dialog';
import { ProviderCreateDialogComponent } from '../create-dialog/create-dialog.component';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { map, shareReplay, startWith, takeUntil, finalize } from 'rxjs/operators';
import { SupportService } from 'src/app/support.service';
import { Provider } from 'src/typings';
import { ProviderService } from '../provider.service';
import type { PartsSelectionAvailabilityKind } from '../../parts-selection/parts-selection.service';

export interface ProviderSelectorSuggestion {
  provider_id: number | null;
  provider_name: string | null;
  quantity: number;
  quantity_label?: string | null;
  purchase_price?: number | null;
  availability_snapshot?: string | null;
  availability_kind?: PartsSelectionAvailabilityKind | null;
  arrival_label?: string | null;
  covers_need?: boolean;
}

@Component({
  selector: 'app-provider-selector',
  templateUrl: './selector.component.html',
  styleUrls: ['./selector.component.scss']
})
export class SelectorComponent implements OnInit, OnDestroy {
  constructor(
    private providerService: ProviderService,
    private dialog: MatDialog,
    public supportService: SupportService
  ) {}

  private readonly createdProviders = new BehaviorSubject<Provider[]>([]);
  private readonly destroy$ = new Subject<void>();
  creating = false;

  providers: Observable<Provider[]> = combineLatest([
    this.providerService.getAllProviders(), this.createdProviders
  ]).pipe(
    map(([providers, created]) => [...created, ...providers.filter(provider => !created.some(item => item.id === provider.id))]),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  filteredOptions: Observable<Provider[]> = of([]);

  filteredSuggestions: Observable<ProviderSelectorSuggestion[]> = of([]);

  private readonly suggestionsSubject = new BehaviorSubject<ProviderSelectorSuggestion[]>([]);

  value: any;

  @Input() control: UntypedFormControl = new UntypedFormControl();

  @Input() loadOnFocus = false;

  optionsActivated = false;

  @Input() disabled: boolean = true;

  @Input() balance = false;

  @Input() appearance: MatFormFieldAppearance = 'outline';

  @Input() label = 'Поставщик';

  @Input() allowCreate = true;

  @Input() showCreateButton = false;

  @Input() createDisabled = false;

  get createName() {
    return typeof this.control.value === 'string' ? this.control.value.trim() : '';
  }

  createProvider() {
    if (!this.allowCreate || this.createDisabled || this.control.disabled || this.creating) return;
    this.creating = true;
    this.dialog.open(ProviderCreateDialogComponent, {
      width: '560px', maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100vh - 32px)',
      data: { description: this.createName }
    }).afterClosed().pipe(
      takeUntil(this.destroy$), finalize(() => this.creating = false)
    ).subscribe((provider: Provider | undefined) => {
      if (!provider?.id) return;
      this.createdProviders.next([provider, ...this.createdProviders.value]);
      this.control.setValue(provider);
      this.control.markAsDirty();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.createdProviders.complete();
    this.suggestionsSubject.complete();
  }

  @Input() suggestionsLoading = false;

  @Input() panelWidth: string | number = 'auto';

  @Input()
  set suggestions(value: ProviderSelectorSuggestion[] | null | undefined) {
    this.suggestionsSubject.next(value || []);
  }

  public displayProperty(provider: Provider | ProviderSelectorSuggestion | string | null) {
    if (typeof provider === 'string') return provider;
    return provider
      ? ('description' in provider ? provider.description : provider.provider_name) || ''
      : '';
  }

  ngOnInit(): void {
    const query = this.control.valueChanges.pipe(
      startWith(this.control.value || ''),
      map((value) => this.getQuery(value))
    );

    this.filteredSuggestions = combineLatest([query, this.suggestionsSubject]).pipe(
      map(([search, suggestions]) =>
        suggestions.filter((suggestion) => this.matches(search, suggestion.provider_name || ''))
      )
    );

    this.filteredOptions = combineLatest([query, this.providers, this.suggestionsSubject]).pipe(
      map(([search, providers, suggestions]) => {
        const suggestedProviderIds = new Set(
          suggestions.map((suggestion) => Number(suggestion.provider_id || 0)).filter(Boolean)
        );
        return providers.filter(
          (provider) =>
            !suggestedProviderIds.has(Number(provider.id)) &&
            this.matches(search, provider.description)
        );
      })
    );
  }

  getSuggestionAvailability(suggestion: ProviderSelectorSuggestion) {
    if (!(suggestion.quantity > 0)) return 'Нет данных о наличии';
    const place: Record<PartsSelectionAvailabilityKind, string> = {
      LOCAL: 'На нашем складе',
      OTHER_LOCAL: 'Другой наш склад',
      IN_CITY: 'В городе',
      OUT_OF_CITY: 'За городом'
    };
    const availability =
      suggestion.quantity_label ||
      suggestion.availability_snapshot ||
      `${suggestion.quantity} шт.`;
    return [suggestion.availability_kind && place[suggestion.availability_kind], availability]
      .filter(Boolean)
      .join(' · ');
  }

  private getQuery(value: string | Provider | ProviderSelectorSuggestion | null) {
    return typeof value === 'string' ? value.trim().toLowerCase() : '';
  }

  private matches(query: string, value: string) {
    return !query || value.toLowerCase().includes(query);
  }

  onSelect(event: MatAutocompleteSelectedEvent) {
    const value = event.option.value;
    if (value && typeof value === 'object' && 'provider_id' in value) {
      const suggestion = value as ProviderSelectorSuggestion;
      if (suggestion.provider_id) {
        this.control.setValue({
          id: Number(suggestion.provider_id),
          description: suggestion.provider_name || ''
        } as Provider);
      }
      return;
    }

    if (typeof value === 'string') {
      if (value.trim()) this.createProvider();
    }
  }
}
